import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import CredentialsModal from "@/components/CredentialsModal";
import { Plus, Building2, Pause, Play, Calendar, Trash2 } from "lucide-react";

const emptyForm = {
  name: "", address: "", email: "", phone: "", logo_url: "",
  plan: "trial", status: "trial",
  admin_name: "", admin_email: "", admin_password: "",
};

export default function Companies() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/super-admin/companies");
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.admin_email) delete payload.admin_email;
      if (!payload.admin_password) delete payload.admin_password;
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/super-admin/companies", payload);
      toast.success("Company created");
      setOpen(false);
      setForm(emptyForm);
      if (data.initial_admin) {
        setCreds({
          email: data.initial_admin.email,
          password: data.initial_admin.password,
          email_requested: false,
        });
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = async (id) => {
    try {
      await api.post(`/super-admin/companies/${id}/suspend`);
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const extend = async (id) => {
    try {
      await api.post(`/super-admin/companies/${id}/extend`, null, { params: { days: 30 } });
      toast.success("Extended 30 days");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this company and ALL its data? This cannot be undone.")) return;
    try {
      await api.delete(`/super-admin/companies/${id}`);
      toast.success("Company deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-companies-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Tenants</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Client Companies</h1>
          <p className="text-sm text-zinc-500 mt-1">Add, suspend, extend or remove shipping/logistics clients.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-btn" className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5">
              <Plus className="h-4 w-4 mr-2" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight">Add a client company</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <Field label="Company name *">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="co-name" />
              </Field>
              <Field label="Contact email">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
              </Field>
              <Field label="Logo URL (optional)">
                <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="rounded-sm" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Address">
                  <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" />
                </Field>
              </div>
              <Field label="Plan">
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="co-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (14 days, 5 emp)</SelectItem>
                    <SelectItem value="starter">Starter (10 emp · ₹999/mo)</SelectItem>
                    <SelectItem value="pro">Pro (50 emp · ₹2999/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (unlimited)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <div className="md:col-span-2 mt-2 pt-4 border-t border-zinc-200">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Initial admin account</div>
              </div>
              <Field label="Admin name">
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="rounded-sm" />
              </Field>
              <Field label="Admin email *">
                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="rounded-sm" data-testid="co-admin-email" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Admin password (leave blank to auto-generate)">
                  <Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="rounded-sm font-mono" placeholder="auto-generated if empty" />
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="co-save" className="btn-pressable rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
                {saving ? "Creating…" : "Create company"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Company</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Plan</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Employees</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Invoices</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expires</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-16 text-sm text-zinc-500">
                  <Building2 className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
                  No client companies yet. Add your first tenant.
                </TableCell>
              </TableRow>
            )}
            {list.map((c) => (
              <TableRow key={c.id} className="hover:bg-zinc-50" data-testid={`co-row-${c.id}`}>
                <TableCell>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{c.slug}</div>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-zinc-900 text-white rounded-sm">{c.plan}</span>
                </TableCell>
                <TableCell>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                    c.status === "active" ? "bg-emerald-100 text-emerald-700" :
                    c.status === "trial"  ? "bg-amber-100 text-amber-700" :
                    c.status === "suspended" ? "bg-red-100 text-red-700" :
                    "bg-zinc-100 text-zinc-600"
                  }`}>{c.status}</span>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{c.employee_count}/{c.employee_limit === -1 ? "∞" : c.employee_limit}</TableCell>
                <TableCell className="text-right font-mono text-sm">{c.invoice_count}</TableCell>
                <TableCell className="font-mono text-xs">{c.expires_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => extend(c.id)} title="Extend 30 days" data-testid={`co-extend-${c.id}`}>
                    <Calendar className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleSuspend(c.id)} title="Toggle suspend" data-testid={`co-suspend-${c.id}`}>
                    {c.status === "suspended" ? <Play className="h-3.5 w-3.5 text-emerald-600" /> : <Pause className="h-3.5 w-3.5 text-amber-600" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} title="Delete" data-testid={`co-delete-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CredentialsModal open={!!creds} onClose={() => setCreds(null)} credentials={creds} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
